import { useState } from 'react';
import { Copy, Check, Terminal, Bot, Zap, ExternalLink, Sparkles } from 'lucide-react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const MCP_URL = 'https://api.linkdiry.com/mcp';

const CLAUDE_DESKTOP_CONFIG = `{
  "mcpServers": {
    "linkdiry": {
      "url": "${MCP_URL}"
    }
  }
}`;

const CLAUDE_CODE_CMD = `claude mcp add linkdiry ${MCP_URL}`;

const CURL_EXAMPLE = `curl -sX POST ${MCP_URL} \\
  -H "Content-Type: application/json" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/call",
    "params": {
      "name": "search_domains",
      "arguments": { "max_price": 200, "min_dr": 40 }
    }
  }'`;

const TOOLS: Array<{ name: string; description: string; example: string }> = [
  {
    name: 'search_domains',
    description: 'Find domains matching structured filters (DR, price, country, traffic, etc.)',
    example: '"Find 20 tech domains in the US under $200 with DR 40+"',
  },
  {
    name: 'get_domain_prices',
    description: 'Full price breakdown for one domain across every marketplace',
    example: '"What\'s the cheapest price for a guest post on bbc.com?"',
  },
  {
    name: 'bulk_lookup',
    description: 'Look up prices for up to 50 domains at once',
    example: '"Here\'s my competitor\'s backlinks — find where I can buy the same sites"',
  },
  {
    name: 'score_relevance',
    description: 'AI topical relevance score for placing a link on a domain for a keyword',
    example: '"Rank these 10 domains by relevance for the keyword ‘fintech compliance’"',
  },
  {
    name: 'parse_natural_language_query',
    description: 'Turn a phrase into structured search filters',
    example: 'Used internally by agents before calling search_domains',
  },
  {
    name: 'list_marketplaces',
    description: 'Every marketplace LinkDiry aggregates from',
    example: '"Which marketplaces do you pull data from?"',
  },
  {
    name: 'get_market_stats',
    description: 'Aggregate inventory size, seller count, average price',
    example: '"How many domains do you track? What\'s the average guest post price?"',
  },
];

export default function McpServer() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="LinkDiry MCP Server - Guest post price data for AI agents"
        description="Expose LinkDiry's guest post and backlink price data to Claude, ChatGPT, and other AI agents via the Model Context Protocol (MCP)."
        canonical="/mcp-server"
      />
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 aurora pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 pt-20 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 border border-brand-100 text-brand-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
            <Bot className="w-3.5 h-3.5" /> For AI agents
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-[1.05] mb-5">
            Plug <span className="gradient-text">LinkDiry</span> into any AI agent
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6 leading-relaxed">
            LinkDiry ships an MCP (Model Context Protocol) server so agents built on Claude, ChatGPT, or
            any MCP-compatible client can query guest post prices and score backlink relevance directly —
            no scraping, no extra API integration.
          </p>
          <p className="text-sm text-slate-500">
            Remote HTTP endpoint · No install · No API key required for read-only tools
          </p>
        </div>
      </section>

      {/* Quick connect */}
      <section className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Connect in 30 seconds</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <ConnectCard
            title="Claude Desktop / Claude Code"
            icon={Sparkles}
            description="Add LinkDiry to your config file:"
            code={CLAUDE_DESKTOP_CONFIG}
            hint="Drop this into ~/.config/claude-desktop/config.json (or the equivalent on your OS). Restart the app."
          />
          <ConnectCard
            title="Via Claude Code CLI"
            icon={Terminal}
            description="One command to register:"
            code={CLAUDE_CODE_CMD}
            hint="Agents instantly have access to every tool below."
          />
        </div>
      </section>

      {/* Tools */}
      <section className="max-w-5xl mx-auto px-4 py-14 border-t border-slate-100 mt-8">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-3">Available tools</p>
          <h2 className="text-3xl font-bold text-slate-900">Seven read-only tools your agent can call</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {TOOLS.map(t => (
            <div key={t.name} className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-brand-200 hover:shadow-card-hover transition-all">
              <code className="inline-block px-2 py-0.5 text-xs font-mono font-bold text-brand-700 bg-brand-50 border border-brand-100 rounded">
                {t.name}
              </code>
              <p className="text-sm text-slate-700 mt-3 mb-2 leading-relaxed">{t.description}</p>
              <p className="text-xs text-slate-500 italic">User says: {t.example}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Raw HTTP example */}
      <section className="bg-slate-50 py-14 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-3">Not using an MCP client?</p>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Call it with plain HTTP</h2>
            <p className="text-slate-600">Every tool is reachable as a JSON-RPC 2.0 POST. No SDK required.</p>
          </div>
          <CodeBlock code={CURL_EXAMPLE} language="bash" />
          <p className="text-xs text-slate-500 text-center mt-4">
            See the full JSON-RPC spec at{' '}
            <a href={MCP_URL} target="_blank" rel="noopener noreferrer" className="text-brand-600 underline inline-flex items-center gap-0.5">
              {MCP_URL} <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: 'Real data, not scraping', body: 'Your agent queries the same partner-fed inventory we show on linkdiry.com. No fragile selectors.' },
            { icon: Bot, title: 'Works with every client', body: 'Claude Desktop, Claude Code, OpenAI Agents SDK, custom stacks. Anything that speaks MCP.' },
            { icon: Sparkles, title: 'AI relevance built in', body: "Our Phase-2 relevance model is exposed as a tool — agents can weigh topical fit, not just DR." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="p-5">
              <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-brand-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-1.5">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ConnectCard({
  title, icon: Icon, description, code, hint,
}: {
  title: string;
  icon: typeof Sparkles;
  description: string;
  code: string;
  hint: string;
}) {
  return (
    <div className="p-5 rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-brand-600" />
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">{description}</p>
      <CodeBlock code={code} />
      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">{hint}</p>
    </div>
  );
}

function CodeBlock({ code, language }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="relative group">
      <pre className="p-3.5 pr-11 rounded-lg bg-slate-900 text-slate-100 text-xs leading-relaxed overflow-x-auto border border-slate-800">
        <code>{code}</code>
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
        aria-label="Copy code"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      {language && (
        <span className="absolute top-2 left-3 text-[10px] uppercase tracking-wider font-mono text-slate-500">
          {language}
        </span>
      )}
    </div>
  );
}
